import { useMemo, useState } from 'react';
import GTable from '../../components/elements/GTable';
import useUserAccount from '../../hooks/userAccountHook';
import { getFormattedAccessRight, getPhoneNumberFromShortCodeFormat, sentenceCase, showToast } from '../../utils/miscFunctions';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import PopOnMe from '../../components/elements/poponme';
import { disableAdminAccount, generateAgentLink } from '../../api/requests';
import { Staff } from '../../models/staff/Staff';
import { ITableRowAction } from '../../components/elements/GTable/GTable';
import useModal from '../../hooks/modalHook';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Loader from '../../components/Loader';
import usePartner from '../../hooks/partnerHook';
import { useNavigate } from 'react-router-dom';
import useRoute from '../../hooks/routesHook';
import { Ban, CheckCircle, Key, Link2, Lock, RotateCw, UserMinus, UserPlus } from 'lucide-react';
import ActionConfirmation from '../../components/elements/ActionConfirmation';
import moment from 'moment';
import UsersStatus from '../../components/elements/UsersStatus';

interface ITeamMembers {
  teamID?: number;
}

const TeamMembers = ({ teamID }: ITeamMembers) => {
  const { user } = useUserAccount();
  const { assignMemberToTeam, PartnerModal, getAllCompanyStaff, setUserPermission, showAgentLinkModal, resendInviteEmail, changeMemberAccessRight, assignStaffToBranchModal, deleteStaffFromBranchModal } = usePartner();
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const id = teamID ? teamID : user.company?.id; 
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const queryClient = useQueryClient();

   const { data: teamStaff, isLoading } = useQuery({
    queryKey: ['getAllCompanyStaff', id],
    queryFn: () => getAllCompanyStaff(Number(id)),
  })

  const sortedTeamStaffs = teamStaff && [...teamStaff]
    .sort((a:any, b:any) => Number(b.id) - Number(a.id))
    .filter((staff:any) => (user.isBroker() || user.isInsurer()) ? !staff.email.includes('brolly.africa') : true);

  const setTeamStatus = (data: any) => {
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <ActionConfirmation
          type={data?.activated ? "warning" : "success"}
          onClose={closeModal}
          action={data?.activated ? "deactivate" : "activate"}
          onConfirm={() => {
            const agentId = data?.id;
            disableAdminAccount(agentId, !data?.activated)
              .then((response: any) => {
                showToast.success(response?.message || `Agent account ${data.activated ? 'deactivated' : 'activated'} successfully`);
                getAllCompanyStaff();
              })
              .catch((error: any) => {
                showToast.error(error.description);
              }).finally(() => { closeModal(); queryClient.invalidateQueries({ queryKey: ['getAllCompanyStaff'] }) })
          }}
        />
      ),
      showTitleSection: true,
      classes: '!p-0 r-[5px]',
      title: data?.activated ? 'Deactivate User' : 'Activate User'
    });
  };

  const getTableRowActions = (data: any): ITableRowAction[] => {
    const actions = [];
    // Admins and Agents cannot change roles/permissions or send team invitations
    const canManageTeam = !user.isAdministrator() && !user.isAgent();
    if (data) {



      if (data.activated) {
        if (canManageTeam) {
          actions.push(
            {
              label: 'Change Role',
              icon: <Key className='h-4 w-4' />,
              callback: () => {
                changeMemberAccessRight(data);
              },
              data: data
            },
            {
              label: 'Set Permissions',
              icon: <Lock className='h-4 w-4' />,
              callback: () => {
                setUserPermission(data);
              },
              data: data
            }
          )
        }
        actions.push(
          {
            label: data.branchName ? 'Remove From Branch' : 'Assign To Branch',
            icon: data.branchName ? <UserMinus className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />,
            callback: async () => {
              if (data.branchName) {
                deleteStaffFromBranchModal(data.id, data.branchName);
              } else {
                assignStaffToBranchModal(data.id);
              }

            },
            data: data
          }
        )
      }

      if (user.isInternalOwner() || user.isInternalSupervisor() || user.isInsurer() || user.isOwner() || user.isBroker())
        actions.push(
          {
            label: data.activated ? 'Deactivate' : 'Activate',
            icon: data.activated ? <Ban className="h-4 w-4" /> : <CheckCircle className='h-4 w-4' />,
            callback: async () => {
              setTeamStatus(data)

            },
            data: data
          },
        );

      if (!data.activated && canManageTeam)
        actions.push(
          {
            label: 'Resend Invite Email',
            icon: <RotateCw className="h-4 w-4" />,
            callback: async () => {
              try {
                resendInviteEmail(data.id);
              } catch (error) { }
            },
            data: data
          },
          {
            label: 'Generate Agent Link',
            icon: <Link2 className="h-4 w-4" />,
            callback: async () => {
              try {
                const agentId = data?.id;
                let res = await generateAgentLink(agentId);
                if (res) showAgentLinkModal(res);
                showToast.success('Agent invite link generated successfully');
              } catch (error) { }
            },
            data: data
          },
        )
    }

    return actions;
  };

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'name',
            Cell: (row: any) => {
              return <span>{row.cell.value ? sentenceCase(row.cell.value) : '-'}</span>;
            }
          },
          {
            Header: 'Branch',
            accessor: 'branchName',
            Cell: (row: any) => {
              return <span>{row.cell.value ? sentenceCase(row.cell.value) : '-'}</span>;
            }
          },
          {
            Header: 'Email',
            accessor: 'email',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Phone',
            accessor: 'phone',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Last seen',
            accessor: 'lastSeen',
            Cell: (row: any) => {
              return <span>{moment(row.cell.value).format('DD/MM/YYYY') ?? '-'}</span>;
            }
          },
          {
            Header: 'Role',
            accessor: 'role',
            Cell: (row: any) => {
              return (
                <UsersStatus
                  status={
                    row.cell.value ? getFormattedAccessRight(row.cell.value) : '-'
                  }
                  classes="px-2 !w-fit "
                />
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'activated',
            Cell: (row: any) => {
              return (
                <div className="">
                  {row.cell.value ? (
                    <div className="text-xs text-[#0F7125] px-2 w-fit border border-[#0F7125] flex  items-center justify-center rounded-full ">
                      Active
                    </div>
                  ) : (
                    <div className="text-xs text-[#FBC02D] px-2 w-fit border border-[#FBC02D] flex  items-center justify-center rounded-full ">
                      <span>Inactive</span>
                    </div>
                  )}
                </div>
              );
            }
          },
          {
            Header: 'Verified',
            accessor: 'verified',
            Cell: (row: any) => {
              return (
                <div className="">
                  {row.cell.value ? (
                    <div className="text-xs text-[#0F7125] px-2 w-fit border border-[#0F7125] flex  items-center justify-center rounded-full ">
                      Verified
                    </div>
                  ) : (
                    <div className="text-xs text-red-700 px-2 w-fit border border-red-700 flex  items-center justify-center rounded-full ">
                      <span>Not verified</span>
                    </div>
                  )}
                </div>
              );
            }
          },
          {
            id: 'Action',
            Header: 'Actions',
            Cell: (row: any) => {
              return (
                !user.isRideSharing() && !user.isInsurer() && <div className="cursor-pointer flex justify-center">
                  <PopOnMe actions={getTableRowActions(row.row.original)}>
                    <EllipsisVerticalIcon className="h-4 w-4" />
                  </PopOnMe>
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );

 
  return (
    <div className='p-5'>
      {isLoading ?
      
          <Loader message="Fetching all users. Please wait..." brollyLoaderFixed  />
       
        :
        <div className="overflow-x-auto ">
          <GTable
            columns={columns}
            data={sortedTeamStaffs && sortedTeamStaffs}
            isRowLinked={false}
            tableType={'team members'}
            onRowClick={(staff: Staff) => {
              if (!user.isInternalAdmin()) return
              const managerId = staff.id;
              if (managerId)
                navigate(`${getDashboardBase()}/teams/staff/${managerId}`);
            }}
            showSearch={false}
            classes={'h-fit'}
          />
        </div>
      }
      <PartnerModal />
      <ModalComponent />
    </div>
  );
};

export default TeamMembers;
